// Trading during a draft (DRAFT_ROOM.md "Trading during the draft", B-345).
//
// Sleeper's way, with Yahoo's rule: two managers in the room swap picks not
// yet made and players already drafted, the same number each side, and the
// trade happens the moment it is accepted. The same number each side is what
// keeps every roster at the size its draft fills: a team holds its rounds'
// worth of picks and players from the first pick to the last, so a trade of
// two for two moves nothing but who has what.
//
// PURE, LIKE THE ENGINE AROUND IT. An offer waits in the state until it is
// answered, withdrawn or no longer true; every change to the board sweeps out
// the offers it made untrue, so an offer that is still there can be accepted.

import type { Applied, DraftEvent, DraftState } from './engine';
import { holderOf, overallOf, ownerOf, pickKey } from './order';

/**
 * One thing in a trade: a pick not yet made, by its round and the team whose
 * slot it is (`teamId`, which a trade never changes), or a player drafted in
 * this draft.
 */
export type TradeItem =
  { kind: 'pick'; round: number; teamId: string } | { kind: 'player'; playerId: string };

/** A trade put to another manager: `give` goes from `fromTeamId` to `toTeamId`, and `get` comes back. */
export interface DraftOffer {
  id: string;
  fromTeamId: string;
  toTeamId: string;
  give: TradeItem[];
  get: TradeItem[];
  /** When it was made, server time. */
  at: number;
}

/** A trade accepted. */
export interface DraftTrade extends DraftOffer {
  /** 1, 2, … in the order accepted, never reused: a trade after an undo has a new number. */
  seq: number;
  acceptedAt: number;
}

/** How an offer stopped waiting. */
export type OfferEnd = 'accepted' | 'declined' | 'withdrawn' | 'countered' | 'void';

/**
 * The most offers a team may have waiting at once. Enough to shop a pick
 * round the room; few enough that nobody's screen fills with one manager's.
 */
export const MAX_OPEN_OFFERS = 5;

type Rejected = Extract<DraftEvent, { type: 'rejected' }>;
type Problem = { code: Rejected['code']; message: string };

function reject(state: DraftState, problem: Problem): Applied {
  return { state, events: [{ type: 'rejected', ...problem }] };
}

/** A key that is the same for the same thing, to find one named twice. */
function itemKey(item: TradeItem): string {
  return item.kind === 'pick'
    ? `pick:${pickKey(item.round, item.teamId)}`
    : `player:${item.playerId}`;
}

/** The overall number of a pick item, or null for one this draft does not have. */
export function overallOfItem(
  state: DraftState,
  item: Extract<TradeItem, { kind: 'pick' }>,
): number | null {
  if (!Number.isInteger(item.round) || item.round < 1 || item.round > state.rounds) return null;
  return overallOf(state, item.round, item.teamId);
}

/** Who has this thing now; null for a pick already made or a player not drafted. */
export function heldBy(state: DraftState, item: TradeItem): string | null {
  if (item.kind === 'pick') {
    const overall = overallOfItem(state, item);
    if (overall === null || overall <= state.picks.length) return null;
    return ownerOf(state, overall);
  }
  const pick = state.picks.find((p) => p.playerId === item.playerId);
  return pick === undefined ? null : holderOf(state, pick);
}

/** Why one side's things cannot go in a trade, or null. */
function sideProblem(
  state: DraftState,
  items: readonly TradeItem[],
  teamId: string,
  whose: 'yours' | 'theirs',
): Problem | null {
  for (const item of items) {
    if (item.kind === 'pick' && overallOfItem(state, item) === null) {
      return { code: 'bad-trade', message: 'That pick is not in this draft.' };
    }
    // A KEEPER'S PICK IS SPENT before the draft (PLAN-2026-10-27-the-next-
    // season §2.3): the player on it is the team's already.
    if (
      item.kind === 'pick' &&
      (state.keepers ?? []).some(
        (k) => k.round === item.round && (k.slotTeamId ?? k.teamId) === item.teamId,
      )
    ) {
      return { code: 'bad-trade', message: 'That pick is a keeper’s.' };
    }
    const holder = heldBy(state, item);
    if (holder === null) {
      return item.kind === 'pick'
        ? { code: 'stale-trade', message: 'A pick in that trade has been made.' }
        : { code: 'bad-trade', message: 'A player in that trade has not been drafted.' };
    }
    if (holder !== teamId) {
      return {
        code: 'stale-trade',
        message:
          item.kind === 'pick'
            ? `One of those picks is not ${whose} to trade now.`
            : `One of those players is not ${whose} to trade now.`,
      };
    }
  }
  return null;
}

/** Why this trade cannot happen now, or null when it can. */
export function tradeProblem(
  state: DraftState,
  deal: Pick<DraftOffer, 'fromTeamId' | 'toTeamId' | 'give' | 'get'>,
): Problem | null {
  if (!state.trading) {
    return { code: 'bad-trade', message: 'This draft does not take trades.' };
  }
  if (state.status === 'complete') return { code: 'not-live', message: 'The draft is over.' };
  const { fromTeamId: from, toTeamId: to } = deal;
  if (!state.order.includes(from) || !state.order.includes(to)) {
    return { code: 'unknown-team', message: 'That team is not in this draft.' };
  }
  if (from === to) return { code: 'bad-trade', message: 'A trade takes two teams.' };
  if (state.ai[from] || state.ai[to]) {
    return { code: 'bad-trade', message: 'Bots do not trade in the draft room.' };
  }
  if (deal.give.length === 0 || deal.get.length === 0) {
    return { code: 'bad-trade', message: 'Choose something on each side.' };
  }
  // THE SAME NUMBER EACH SIDE (Yahoo's rule), so no roster ends short or over.
  if (deal.give.length !== deal.get.length) {
    return {
      code: 'bad-trade',
      message: 'Each side gives the same number, so every roster ends the same size.',
    };
  }
  const keys = [...deal.give, ...deal.get].map(itemKey);
  if (new Set(keys).size !== keys.length) {
    return { code: 'bad-trade', message: 'Something is in that trade twice.' };
  }
  return sideProblem(state, deal.give, from, 'yours') ?? sideProblem(state, deal.get, to, 'theirs');
}

/**
 * The offers the board made untrue — a pick in one made, a player in one
 * traded elsewhere or undrafted — taken out, with the event that tells the
 * two teams of each. Every change to the board ends with this.
 */
export function sweepOffers(state: DraftState): Applied {
  const offers = state.offers ?? [];
  const dead = offers.filter((o) => tradeProblem(state, o) !== null);
  if (dead.length === 0) return { state, events: [] };
  return {
    state: { ...state, offers: offers.filter((o) => !dead.includes(o)) },
    events: [closedEvent(dead.map((offer) => ({ offer, reason: 'void' as const })))],
  };
}

function closedEvent(closed: { offer: DraftOffer; reason: OfferEnd }[]): DraftEvent {
  const teams = new Set<string>();
  for (const { offer } of closed) {
    teams.add(offer.fromTeamId);
    teams.add(offer.toTeamId);
  }
  return { type: 'offers.changed', teamIds: [...teams], closed };
}

/** A new offer, or a counter to one made to this team (`replaces`), which it ends. */
export function offerTrade(
  state: DraftState,
  input: Omit<DraftOffer, 'at'> & { replaces?: string },
  now: number,
): Applied {
  const problem = tradeProblem(state, input);
  if (problem !== null) return reject(state, problem);
  const offers = state.offers ?? [];
  if (offers.some((o) => o.id === input.id)) {
    return reject(state, { code: 'bad-trade', message: 'That offer has been made already.' });
  }
  const replaced =
    input.replaces === undefined ? null : (offers.find((o) => o.id === input.replaces) ?? null);
  if (
    input.replaces !== undefined &&
    (replaced === null ||
      replaced.toTeamId !== input.fromTeamId ||
      replaced.fromTeamId !== input.toTeamId)
  ) {
    return reject(state, { code: 'stale-trade', message: 'That offer is no longer open.' });
  }
  if (offers.filter((o) => o.fromTeamId === input.fromTeamId).length >= MAX_OPEN_OFFERS) {
    return reject(state, {
      code: 'bad-trade',
      message: `You have ${String(MAX_OPEN_OFFERS)} offers waiting already. Withdraw one first.`,
    });
  }
  const offer: DraftOffer = {
    id: input.id,
    fromTeamId: input.fromTeamId,
    toTeamId: input.toTeamId,
    give: [...input.give],
    get: [...input.get],
    at: now,
  };
  const kept = offers.filter((o) => o !== replaced);
  const events: DraftEvent[] = [
    replaced === null
      ? { type: 'offers.changed', teamIds: [offer.fromTeamId, offer.toTeamId], closed: [] }
      : closedEvent([{ offer: replaced, reason: 'countered' }]),
  ];
  return { state: { ...state, offers: [...kept, offer] }, events };
}

type Deal = Pick<DraftOffer, 'fromTeamId' | 'toTeamId' | 'give' | 'get'>;
type Hands = Pick<DraftState, 'owners' | 'holders' | 'picks'>;

/**
 * Things moved to their new teams — `give` to `to` and `get` to `from`, or
 * the other way for an undo. A pick back with its own team, or a player back
 * with the team that drafted him, leaves the map, so "nobody traded it" has
 * one spelling.
 */
function moved<S extends Hands>(state: S, deal: Deal, back: boolean): S {
  const owners = { ...(state.owners ?? {}) };
  const holders = { ...(state.holders ?? {}) };
  const put = (items: readonly TradeItem[], teamId: string) => {
    for (const item of items) {
      if (item.kind === 'pick') {
        const key = pickKey(item.round, item.teamId);
        if (teamId === item.teamId) delete owners[key];
        else owners[key] = teamId;
      } else {
        const pick = state.picks.find((p) => p.playerId === item.playerId);
        if (pick === undefined) continue;
        if (teamId === pick.teamId) delete holders[item.playerId];
        else holders[item.playerId] = teamId;
      }
    }
  };
  put(deal.give, back ? deal.fromTeamId : deal.toTeamId);
  put(deal.get, back ? deal.toTeamId : deal.fromTeamId);
  return { ...state, owners, holders };
}

/**
 * Who has what after these trades, oldest first: for a reader that has a
 * draft's trades and picks but not the room's state, as a finished draft's
 * board does.
 */
export function handsAfter(
  trades: readonly Deal[],
  picks: DraftState['picks'],
  /** The picks that changed hands before the draft (`DraftState.tradedBefore`). */
  before: Readonly<Record<string, string>> = {},
): Pick<DraftState, 'owners' | 'holders'> {
  let hands: Hands = { owners: { ...before }, holders: {}, picks };
  for (const trade of trades) hands = moved(hands, trade, false);
  return { owners: hands.owners ?? {}, holders: hands.holders ?? {} };
}

/**
 * The pick on the clock changed hands: its new owner's turn starts now, for a
 * seat on auto to wait its moment from, and THE CLOCK DOES NOT RESET — the
 * new owner picks with the time that is left, as Sleeper does.
 */
function turnMoved(before: DraftState, after: DraftState, now: number): DraftState {
  if (after.status !== 'live' || after.picks.length >= after.order.length * after.rounds) {
    return after;
  }
  const overall = after.picks.length + 1;
  return ownerOf(before, overall) === ownerOf(after, overall)
    ? after
    : { ...after, turnStartedAt: now };
}

/**
 * The trades that still hold, replayed oldest first over a board with none:
 * for a lobby read again, whose seats may have changed under them. One that
 * no longer holds — a seat closed, a team gone from the order — is dropped,
 * and so is any later one that leaned on it, so whose each pick is stays
 * exactly what the kept trades say. A team that a bot has taken since keeps
 * what it traded for: the trade was made, and a bot inherits a seat as it is.
 */
export function replayTrades(state: DraftState, trades: readonly DraftTrade[]): DraftState {
  // FROM THE PICKS TRADED BEFORE THE DRAFT, which no room trade undoes.
  let cur: DraftState = {
    ...state,
    owners: { ...(state.tradedBefore ?? {}) },
    holders: {},
    trades: [],
  };
  for (const trade of trades) {
    if (tradeProblem({ ...cur, ai: {} }, trade) !== null) continue;
    cur = { ...moved(cur, trade, false), trades: [...(cur.trades ?? []), trade] };
  }
  return cur;
}

/** The answer to an offer made to `teamId`: done at once when accepted. */
export function answerOffer(
  state: DraftState,
  input: { teamId: string; offerId: string; accept: boolean },
  now: number,
): Applied {
  const offers = state.offers ?? [];
  const offer = offers.find((o) => o.id === input.offerId && o.toTeamId === input.teamId);
  if (offer === undefined) {
    return reject(state, { code: 'stale-trade', message: 'That offer is no longer open.' });
  }
  const rest = offers.filter((o) => o !== offer);
  if (!input.accept) {
    return {
      state: { ...state, offers: rest },
      events: [closedEvent([{ offer, reason: 'declined' }])],
    };
  }
  const problem = tradeProblem(state, offer);
  if (problem !== null) return reject(state, problem);
  const seq = (state.tradeSeq ?? 0) + 1;
  const trade: DraftTrade = { ...offer, seq, acceptedAt: now };
  const traded = turnMoved(
    state,
    {
      ...moved(state, offer, false),
      offers: rest,
      trades: [...(state.trades ?? []), trade],
      tradeSeq: seq,
    },
    now,
  );
  const swept = sweepOffers(traded);
  return {
    state: swept.state,
    events: [
      { type: 'traded', trade },
      closedEvent([{ offer, reason: 'accepted' }]),
      ...swept.events,
    ],
  };
}

/** An offer taken back by the team that made it. */
export function withdrawOffer(
  state: DraftState,
  input: { teamId: string; offerId: string },
): Applied {
  const offers = state.offers ?? [];
  const offer = offers.find((o) => o.id === input.offerId && o.fromTeamId === input.teamId);
  if (offer === undefined) {
    return reject(state, { code: 'stale-trade', message: 'That offer is no longer open.' });
  }
  return {
    state: { ...state, offers: offers.filter((o) => o !== offer) },
    events: [closedEvent([{ offer, reason: 'withdrawn' }])],
  };
}

/**
 * The last trade, taken back by the commissioner, while none of its picks has
 * been made. Its players go back too; they can only have moved again in a
 * later trade, and then that one is the last.
 */
export function undoTrade(state: DraftState, now: number): Applied {
  const trades = state.trades ?? [];
  const last = trades[trades.length - 1];
  if (last === undefined) {
    return reject(state, { code: 'nothing-to-undo', message: 'No trade to undo.' });
  }
  if (state.status === 'complete') {
    return reject(state, { code: 'not-live', message: 'The draft is over.' });
  }
  const made = [...last.give, ...last.get].some(
    (item) => item.kind === 'pick' && heldBy(state, item) === null,
  );
  if (made) {
    return reject(state, {
      code: 'stale-trade',
      message: 'A pick in that trade has been made, so the trade stands.',
    });
  }
  // Everything else in it is where the trade put it, or it could not go back.
  if (
    sideProblem(state, last.give, last.toTeamId, 'theirs') !== null ||
    sideProblem(state, last.get, last.fromTeamId, 'theirs') !== null
  ) {
    return reject(state, {
      code: 'stale-trade',
      message: 'Something in that trade has moved since, so the trade stands.',
    });
  }
  const undone = turnMoved(
    state,
    { ...moved(state, last, true), trades: trades.slice(0, -1) },
    now,
  );
  const swept = sweepOffers(undone);
  return {
    state: swept.state,
    events: [{ type: 'trade.undone', trade: last }, ...swept.events],
  };
}
